import React, { useEffect, useRef, useState } from 'react';
import closeIcon from '../../assets/icons/close-icon.svg';
import { CheckboxComponent } from '../buttons/CheckboxComponent';
import CompaniesService from '../../service/CompaniesService';
import { Companies } from '../../common/types/types';
import { companiesToCompaniesTable } from '../../common/utils/HelperFunction';
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks';
import { setDohvaceniCompanies, toggleUpdateCompaniesTable } from '../../store/slice/CompaniesSlice';
import { useLocation, useNavigate } from 'react-router-dom';

interface Props {
  setModalOn: (modalOn: boolean) => void;
  setManageCompanyId: (id: number | string | undefined) => void;
  toastMessage: (message: string) => void;
  companyId: string | number | undefined;
}

export const ManageCompany = ({ setModalOn, setManageCompanyId, toastMessage, companyId }: Props) => {
  const reachedCompanies = useAppSelector((state) => state.companies.dohvaceniCompanies);
  const reachedProcesses = useAppSelector((state) => state.processes.reachedProcesses);
  const [name, setName] = useState<string | null>('');
  const [VAT, setVAT] = useState<string | null>();
  const [keyword, setKeyword] = useState<string | null>('');
  const [altVat, setAltVat] = useState<string | null>('');
  const [clientCode, setClientCode] = useState<string | null>('');
  const [currency, setCurrency] = useState<string | null>('');
  const [group, setGroup] = useState<number | string | null>();
  // const [country, setCountry] = useState<string | null>('');
  const [processTypeId, setProcessTypeId] = useState<string | null>('');
  const [isTrained, setIsTrained] = useState<boolean>(false);

  const [update, setUpdate] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const isFormInvalid = !name || !currency || !group || !VAT;

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // let companyId = location.state?.companyId;
  // if (companyId !== null) companyId = companyId;
  // else companyId = null;

  useEffect(() => {
    if (companyId !== undefined) {
      setUpdate(true);
      reachedCompanies.forEach((company) => {
        if (company.id === companyId) {
          setName(company.name);
          setVAT(company.VAT);
          setKeyword(company.keyword);
          setAltVat(company.altVat);
          setClientCode(company.clientCode);
          setCurrency(company.currency);
          setGroup(company.companyGroup);
          // setCountry(company.country);
          setProcessTypeId(company.processTypeId);
          setIsTrained(company.isTrained);
        }
      });
    }
  }, [reachedCompanies]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (!modalRef.current?.contains(event.target as Node)) {
        setManageCompanyId(undefined);
        setModalOn(false);
      }
    };
    window.addEventListener('mousedown', handleMouseDown);

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setManageCompanyId(undefined);
        setModalOn(false);
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const saveCompany = async (event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    let message = '';
    try {
      if (update) {
        const requestData = {
          id: companyId,
          name,
          VAT,
          keyword,
          altVat,
          clientCode,
          currency,
          companyGroup: group,
          // country,
          processTypeId,
          isTrained,
        };

        await CompaniesService.updateCompany(requestData);
        toastMessage('Changes saved');
        message = 'Error updating company:';
      } else {
        const requestData = {
          name,
          VAT,
          keyword,
          altVat,
          clientCode,
          currency,
          companyGroup: group,
          // country,
          processTypeId,
          isTrained,
        };

        // Use the CompaniesService to add a new company
        await CompaniesService.addCompany(requestData);
        toastMessage('Company created');
        message = 'Error saving company:';
      }
      setManageCompanyId(undefined);
      setModalOn(false);
      dispatch(toggleUpdateCompaniesTable());
    } catch (error) {
      console.error(message, error);
    }
  };

  return (
    <div className='modal-active dark-100'>
      <div className='main-modal'>
        <div className='padding-bottom-5' ref={modalRef}>
          <form onSubmit={saveCompany} className='form modal-form relative manage-company-box'>
            <h3 className='font-h3-regular flex-basis-100'>New Company</h3>
            <img
              className='modal-form__close'
              src={closeIcon}
              alt=''
              onClick={() => {
                setManageCompanyId(undefined);
                setModalOn(false);
              }}
            />
            <div className='form__item'>
              <label className='font-h6-regular margin-left-1' htmlFor='name'>
                Name
              </label>
              <input className='form__item__input font-h5-regular' type='text' onChange={(e) => setName(e.target.value)} value={name || ''} />
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='VAT'>
                VAT
              </label>
              <input className='form__item__input font-h5-regular' onChange={(e) => setVAT(e.target.value)} value={VAT || ''}></input>
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='keyword'>
                Keyword
              </label>
              <input className='form__item__input font-h5-regular' onChange={(e) => setKeyword(e.target.value)} value={keyword || ''}></input>
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='altVat'>
                AltVAT
              </label>
              <input className='form__item__input font-h5-regular' onChange={(e) => setAltVat(e.target.value)} value={altVat || ''}></input>
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='clientCode'>
                Client Code
              </label>
              <input className='form__item__input font-h5-regular' onChange={(e) => setClientCode(e.target.value)} value={clientCode || ''}></input>
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='currency'>
                Currency
              </label>
              <input className='form__item__input font-h5-regular' onChange={(e) => setCurrency(e.target.value)} value={currency || ''}></input>
            </div>
            <div className='form__item relative'>
              <label className='font-h6-regular margin-left-1' htmlFor='group'>
                Group
              </label>
              <select className='form__item__input font-h5-regular max-width-240' name='group' id='group' onChange={(e) => setGroup(e.target.value)}>
                <option value='' disabled selected={group !== 'DOM' && group !== 'INO'}>
                  Choose an option
                </option>
                <option selected={group === 'DOM'} value={'DOM'}>
                  DOM
                </option>
                <option selected={group === 'INO'} value={'INO'}>
                  INO
                </option>
              </select>
            </div>
            {/* <div className='form__item relative'>
          <label className='font-h6-regular margin-left-1' htmlFor='country'>
            Country
          </label>
          <input className='form__item__input font-h5-regular' onChange={(e) => setCountry(e.target.value)} value={country || ''}></input>
        </div> */}
            <div className='form__item relative flex-basis-100'>
              <label className='font-h6-regular margin-left-1' htmlFor='processTypeId'>
                Process Type ID
              </label>
              {/* <input className='form__item__input font-h5-regular' onChange={(e) => setProcessTypeId(e.target.value)} value={processTypeId || ''}></input> */}
              <select
                name=''
                id=''
                className='form__item__input font-h5-regular width-500'
                onChange={(e) => {
                  setProcessTypeId(e.target.value.split(' ')[0]);

                  // setProcessTypeId((prevDocumentVariables) => {
                  //   const updatedDocumentVariables = [...prevDocumentVariables];
                  //   updatedDocumentVariables[index] = { name: variable.name, value: e.target.value };

                  //   return updatedDocumentVariables;
                  // });
                }}
              >
                <option value='' selected={!processTypeId}>
                  Choose an option (or select this one to stay empty)
                </option>
                {reachedProcesses.map((process) => {
                  const currentProcID = process.Name?.split(' ')[0];
                  return (
                    <option selected={currentProcID === processTypeId} key={process.Id} value={process.Name as string}>
                      {process.Name}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* <CheckboxComponent isChecked={isTrained} setIsChecked={setIsTrained} uniqueName={'Is Trained'} type='1' /> */}

            {/* Ukloniti width 100% iz elementa niže kada se vrati checkbox isTrained */}

            <div className='text-right margin-top-3 full-width'>
              <span
                className='button-tertionary font-h5-medium'
                onClick={() => {
                  setManageCompanyId(undefined);
                  setModalOn(false);
                }}
              >
                Cancel
              </span>

              <button disabled={isFormInvalid} className='button-primary margin-left-2'>
                {update ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
